import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {toast} from 'react-toastify';
import { Bot, Plus, MessageCircle, Smartphone, Camera, Pencil, Power, PowerOff, Trash2 } from 'lucide-react';
import agentesService from './services/agentesService';
import NewAgentModal from './components/NewAgentModal';
import { SidebarContext } from '../../shared/context/SidebarContext';


function StatusPill({ status }) {
    const ativo = status === 'ativo';
    return (
        <span className={ativo ? "chip-positive" : "chip-neutral"}>
            <span className={`h-1.5 w-1.5 rounded-full ${ativo ? "bg-positive-400 animate-glow-pulse" : "bg-ink-5"}`} />
            {status}
        </span>
    );
}

function SessaoIcon({ tipo }) {
    if (tipo === 'meta') return <MessageCircle className="h-4 w-4 text-accent-sky" strokeWidth={2} />;
    if (tipo === 'instagram') return <Camera className="h-4 w-4 text-pink-400" strokeWidth={2} />;
    return <Smartphone className="h-4 w-4 text-positive-400" strokeWidth={2} />;
}

function AgenteRow({ agente, onEditar, onAlternarStatus, onExcluir }) {
    return (
        <div className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-white/[0.02] border border-white/[0.04] hover:border-white/[0.08] transition">
            <div className="flex items-center gap-2 min-w-0">
                <Bot className="h-4 w-4 text-brand-300 shrink-0" />
                <div className="min-w-0">
                    <div className="text-sm font-medium text-ink-1 truncate">{agente.nome}</div>
                    <div className="text-xs text-ink-4 tabular-nums">cooldown {agente.cooldown_segundos}s</div>
                </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
                <StatusPill status={agente.status} />
                <button
                    onClick={() => onAlternarStatus(agente)}
                    title={agente.status === 'ativo' ? 'Pausar agente' : 'Ativar agente'}
                    className="p-1.5 rounded-lg hover:bg-white/[0.05] text-ink-3 hover:text-ink-1 transition"
                >
                    {agente.status === 'ativo' ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
                </button>
                <button
                    onClick={() => onEditar(agente)}
                    title="Editar agente"
                    className="p-1.5 rounded-lg hover:bg-white/[0.05] text-ink-3 hover:text-brand-300 transition"
                >
                    <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                    onClick={() => onExcluir(agente)}
                    title="Excluir agente"
                    className="p-1.5 rounded-lg hover:bg-danger-500/10 text-ink-3 hover:text-danger-400 transition"
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
}

function SessaoCard({ sessao, onCriarAgente, onEditar, onAlternarStatus, onExcluir }) {
    const agentes = sessao.agentes || [];
    return (
        <div className="surface-card surface-card-hover p-5 flex flex-col gap-3 min-h-[200px]">
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-xl bg-surface-3 ring-1 ring-white/[0.06] grid place-items-center shrink-0">
                        <SessaoIcon tipo={sessao.sessao_tipo} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-ink-1 truncate">{sessao.nome}</div>
                        <div className="text-xs text-ink-4 flex items-center gap-1.5">
                            <span className="uppercase tracking-wider">{sessao.sessao_tipo}</span>
                            {sessao.numero && <span className="text-ink-5">· {sessao.numero}</span>}
                        </div>
                    </div>
                </div>
                <button
                    onClick={() => onCriarAgente(sessao)}
                    className="chip-brand hover:bg-brand-500/15 transition cursor-pointer shrink-0"
                >
                    <Plus className="h-3 w-3" /> Novo agente
                </button>
            </div>

            <div className="flex-1 flex flex-col gap-1.5">
                {agentes.length === 0 ? (
                    <div className="text-center py-6 text-xs text-ink-5 border-2 border-dashed border-white/[0.06] rounded-xl">
                        Sem agente nesta sessão.
                        <br />
                        <span className="text-ink-5">Clique em "Novo agente" para criar.</span>
                    </div>
                ) : (
                    agentes.map((a) => (
                        <AgenteRow
                            key={a.id}
                            agente={a}
                            onEditar={onEditar}
                            onAlternarStatus={onAlternarStatus}
                            onExcluir={onExcluir}
                        />
                    ))
                )}
            </div>
        </div>
    );
}


export default function AgentesList() {

    const { isOpen } = useContext(SidebarContext);
    const navigate = useNavigate();
    const [sessoes, setSessoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [novoAgenteSessao, setNovoAgenteSessao] = useState(null);


    const carregar = () => {
        setLoading(true);
        return agentesService.cardsSessoes()
            .then((r) => setSessoes(Array.isArray(r.data) ? r.data : []))
            .catch((e) => {
                console.error(e);
                toast.error('Erro ao carregar sessões');
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => { carregar(); }, []);


    const handleCriarAgente = (sessao) => setNovoAgenteSessao(sessao);

    const handleAgenteCriado = (agente) => {
        setNovoAgenteSessao(null);
        toast.success('Agente criado');
        navigate(`/agentes/${agente.id}/editar`);
    };

    const handleEditar = (agente) => navigate(`/agentes/${agente.id}/editar`);

    const handleAlternarStatus = async (agente) => {
        const novo = agente.status === 'ativo' ? 'inativo' : 'ativo';
        try {
            await agentesService.atualizarStatus(agente.id, novo);
            toast.success(`Agente ${novo === 'ativo' ? 'ativado' : 'pausado'}`);
            carregar();
        } catch (e) {
            console.error(e);
            toast.error(e?.response?.data?.error || 'Erro ao atualizar status');
        }
    };

    const handleExcluir = async (agente) => {
        if (!confirm(`Excluir o agente "${agente.nome}"? Esta ação não pode ser desfeita.`)) return;
        try {
            await agentesService.excluir(agente.id);
            toast.success('Agente excluído');
            carregar();
        } catch (e) {
            console.error(e);
            toast.error(e?.response?.data?.error || 'Erro ao excluir agente');
        }
    };


    const totalAgentes = sessoes.reduce((acc, s) => acc + (s.agentes?.length || 0), 0);


    return (
        <div className="min-h-screen text-ink-1 flex relative z-0">
            <div className={`transition-all duration-300 ease-out-expo ${isOpen ? "lg:ml-64" : "sm:ml-16"} w-full max-w-full mb-16 sm:mb-0`}>

                <div className="px-5 sm:px-8 pt-8 pb-12 max-w-[1600px] mx-auto">

                    <header className="mb-8 animate-fade-in">
                        <div className="space-y-1">
                            <span className="chip-brand">
                                <Bot className="h-3 w-3" />
                                Agentes IA
                            </span>
                            <h1 className="display-2 flex items-center gap-3 mt-2">
                                Agentes
                                <Bot className="h-7 w-7 text-brand-400" strokeWidth={2.2} />
                            </h1>
                            <p className="text-ink-3 text-sm">
                                <span className="tabular-nums text-ink-2 font-medium">{totalAgentes}</span> agente{totalAgentes === 1 ? '' : 's'} em <span className="tabular-nums text-ink-2 font-medium">{sessoes.length}</span> sessão{sessoes.length === 1 ? '' : 'ões'} de WhatsApp e Instagram.
                            </p>
                        </div>
                    </header>

                    {loading ? (
                        <div className="surface-card p-20 grid place-items-center text-sm text-ink-4">
                            Carregando...
                        </div>
                    ) : sessoes.length === 0 ? (
                        <div className="surface-card p-12 text-center">
                            <Bot className="h-10 w-10 text-ink-5 mx-auto mb-3" />
                            <h3 className="heading-3 mb-1">Nenhuma sessão WhatsApp ou Instagram</h3>
                            <p className="text-sm text-ink-4 max-w-md mx-auto">
                                Configure uma sessão Meta (oficial), whatsmeow ou Instagram em WhatsApp → Sessões / Oficial / Instagram para começar.
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 animate-slide-up">
                            {sessoes.map((s) => (
                                <SessaoCard
                                    key={`${s.sessao_tipo}-${s.id}`}
                                    sessao={s}
                                    onCriarAgente={handleCriarAgente}
                                    onEditar={handleEditar}
                                    onAlternarStatus={handleAlternarStatus}
                                    onExcluir={handleExcluir}
                                />
                            ))}
                        </div>
                    )}

                </div>
            </div>

            {novoAgenteSessao && (
                <NewAgentModal
                    sessao={novoAgenteSessao}
                    onClose={() => setNovoAgenteSessao(null)}
                    onCreated={handleAgenteCriado}
                />
            )}
        </div>
    );
}
